import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Pill, TrendingUp, TrendingDown, Minus, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface ActivityRecord {
  id: string;
  patient_id: string;
  timestamp: string;
  category: string;
  activity_type: string;
  description: string;
  systolic?: number;
  diastolic?: number;
  heart_rate?: number;
}

interface ActivitySummary {
  total_records: number;
  medication_adherence_rate: number;
  latest_bp: {
    systolic: number;
    diastolic: number;
    heart_rate: number;
    timestamp: string;
  } | null;
  bp_readings_count: number;
  medication_count: number;
}

interface ActivityDashboardProps {
  activities: ActivityRecord[];
  summary: ActivitySummary | null;
}

const ActivityDashboard: React.FC<ActivityDashboardProps> = ({ activities, summary }) => {
  const { t } = useTranslation();

  // Prepare BP trend data for chart
  const bpTrendData = React.useMemo(() => {
    const bpActivities = activities
      .filter(a => a.category === 'vital_signs' && a.activity_type === 'bp_reading' && a.systolic && a.diastolic)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      .slice(-14); // Last 14 readings

    return bpActivities.map(activity => ({
      date: new Date(activity.timestamp).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' }),
      timestamp: activity.timestamp,
      systolic: activity.systolic,
      diastolic: activity.diastolic,
      heartRate: activity.heart_rate
    }));
  }, [activities]);

  // Calculate average BP
  const averageBP = React.useMemo(() => {
    const bpActivities = activities.filter(a => a.systolic && a.diastolic);
    if (bpActivities.length === 0) return null;

    const avgSystolic = Math.round(
      bpActivities.reduce((sum, a) => sum + (a.systolic || 0), 0) / bpActivities.length
    );
    const avgDiastolic = Math.round(
      bpActivities.reduce((sum, a) => sum + (a.diastolic || 0), 0) / bpActivities.length
    );

    return { systolic: avgSystolic, diastolic: avgDiastolic };
  }, [activities]);

  // Calculate BP trend (comparing last 3 vs previous 3 readings)
  const bpTrend = React.useMemo(() => {
    const bpActivities = activities
      .filter(a => a.systolic && a.diastolic)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    if (bpActivities.length < 6) return 'stable';

    const recent3 = bpActivities.slice(0, 3);
    const previous3 = bpActivities.slice(3, 6);

    const recentAvg = recent3.reduce((sum, a) => sum + (a.systolic || 0), 0) / 3;
    const previousAvg = previous3.reduce((sum, a) => sum + (a.systolic || 0), 0) / 3;

    const diff = recentAvg - previousAvg;

    if (diff > 5) return 'increasing';
    if (diff < -5) return 'decreasing';
    return 'stable';
  }, [activities]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="h-4 w-4 text-red-600" />;
      case 'decreasing':
        return <TrendingDown className="h-4 w-4 text-green-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'decreasing':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (!summary) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Records */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
        >
          <Card className="border-none shadow-lg bg-gradient-to-br from-indigo-500 to-indigo-700 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 font-medium">{t('patientActivity.summary.totalRecords')}</p>
                  <p className="text-3xl font-bold mt-2">{summary.total_records}</p>
                </div>
                <Calendar className="h-12 w-12 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Medication Adherence */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-500 to-blue-700 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 font-medium">{t('patientActivity.summary.medicationAdherence')}</p>
                  <p className="text-3xl font-bold mt-2">{summary.medication_adherence_rate}%</p>
                </div>
                <Pill className="h-12 w-12 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Latest BP */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-none shadow-lg bg-gradient-to-br from-red-500 to-red-700 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90 font-medium">{t('patientActivity.summary.latestBP')}</p>
                  {summary.latest_bp ? (
                    <p className="text-3xl font-bold mt-2">
                      {summary.latest_bp.systolic}/{summary.latest_bp.diastolic}
                    </p>
                  ) : (
                    <p className="text-2xl font-medium mt-2 opacity-70">--/--</p>
                  )}
                </div>
                <Activity className="h-12 w-12 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* BP Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-none shadow-lg">
            <CardContent className="p-6">
              <div className="space-y-2">
                <p className="text-sm text-gray-600 font-medium">{t('patientActivity.summary.bpTrend')}</p>
                {averageBP && (
                  <p className="text-2xl font-bold text-gray-900">
                    {averageBP.systolic}/{averageBP.diastolic}
                  </p>
                )}
                <Badge className={`${getTrendColor(bpTrend)} border flex items-center gap-1 w-fit`}>
                  {getTrendIcon(bpTrend)}
                  <span className="capitalize">{bpTrend}</span>
                </Badge>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* BP Trend Chart */}
      {bpTrendData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="border-none shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5 text-red-600" />
                血压趋势图 (Blood Pressure Trend)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={bpTrendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#6b7280"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis 
                      stroke="#6b7280"
                      style={{ fontSize: '12px' }}
                      domain={[60, 180]}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="systolic" 
                      stroke="#ef4444" 
                      strokeWidth={2}
                      name="收缩压 Systolic"
                      dot={{ fill: '#ef4444', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="diastolic" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      name="舒张压 Diastolic"
                      dot={{ fill: '#3b82f6', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    {bpTrendData.some(d => d.heartRate) && (
                      <Line 
                        type="monotone" 
                        dataKey="heartRate" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        name="心率 HR"
                        dot={{ fill: '#10b981', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6 text-center">
            <Pill className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{summary.medication_count}</p>
            <p className="text-sm text-gray-600 mt-1">{t('patientActivity.summary.medicationCount')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <Activity className="h-8 w-8 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{summary.bp_readings_count}</p>
            <p className="text-sm text-gray-600 mt-1">{t('patientActivity.summary.bpReadings')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <Calendar className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">30</p>
            <p className="text-sm text-gray-600 mt-1">{t('patientActivity.summary.last30Days')}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ActivityDashboard;
